import { AttachmentType } from '@@types/api.types';
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENT_SIZE_ERROR_MESSAGE,
} from '@azymmetric/shared/attachments/attachment.constants';
import { zCoercedOptional } from '@utils/zodv4.util';
import { z } from 'zod/v4';

const URL_SCHEME_REGEX = /^[a-zA-Z][a-zA-Z\d+.-]*:/;
const HTTP_SCHEME_REGEX = /^https?:/i;
const HTTP_SCHEME_WITH_SLASHES_REGEX = /^https?:\/\//i;
const INVALID_HOSTNAME_CHARS_REGEX = /[,\s!@#$%^&*()+=[\]{};:'"<>?|\\]/;

type AttachmentSchemaMode = 'create' | 'edit';

export type AttachmentFormData = {
  description?: string;
  file?: File;
  title: string;
  type: AttachmentType;
  url?: string;
};

export const normalizeAttachmentUrl = (url: string): string => {
  const trimmedUrl = url.trim();

  if (trimmedUrl.length === 0) {
    return trimmedUrl;
  }

  if (URL_SCHEME_REGEX.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl.replace(/^\/+/, '')}`;
};

const attachmentBaseSchema = z
  .object({
    description: zCoercedOptional(z.string().optional()),
    file: z.instanceof(File).optional(),
    title: z.string({ message: 'Please enter a title.' }).trim().min(1, { message: 'Please enter a title.' }),
    type: z.enum(AttachmentType),
    url: zCoercedOptional(z.string().optional()),
  })
  .strip();

export const buildAttachmentSchema = (mode: AttachmentSchemaMode) =>
  attachmentBaseSchema
    .transform((value) => {
      if (value.type === AttachmentType.FILE) {
        return {
          ...value,
          url: undefined,
        };
      }

      if (!value.url) {
        return value;
      }

      return {
        ...value,
        url: normalizeAttachmentUrl(value.url),
      };
    })
    .check((ctx) => {
      const value = ctx.value;

      if (value.type === AttachmentType.URL) {
        if (!value.url) {
          ctx.issues.push({
            code: 'custom',
            input: value.url,
            message: 'Please enter a valid URL.',
            path: ['url'],
          });
          return;
        }

        if (HTTP_SCHEME_REGEX.test(value.url) && !HTTP_SCHEME_WITH_SLASHES_REGEX.test(value.url)) {
          ctx.issues.push({
            code: 'custom',
            input: value.url,
            message: 'Please enter a valid URL.',
            path: ['url'],
          });
          return;
        }

        if (!z.url({ message: 'Please enter a valid URL.' }).safeParse(value.url).success) {
          ctx.issues.push({
            code: 'custom',
            input: value.url,
            message: 'Please enter a valid URL.',
            path: ['url'],
          });
          return;
        }

        try {
          const { hostname } = new URL(value.url);

          if (INVALID_HOSTNAME_CHARS_REGEX.test(hostname) || !hostname.includes('.')) {
            ctx.issues.push({
              code: 'custom',
              input: value.url,
              message: 'Please enter a valid URL.',
              path: ['url'],
            });
          }
        } catch {
          ctx.issues.push({
            code: 'custom',
            input: value.url,
            message: 'Please enter a valid URL.',
            path: ['url'],
          });
        }
      }

      if (value.type === AttachmentType.FILE) {
        if (mode === 'create' && !value.file) {
          ctx.issues.push({
            code: 'custom',
            input: value.file,
            message: 'Please select a file to upload.',
            path: ['file'],
          });
          return;
        }

        if (value.file && value.file.size > MAX_ATTACHMENT_SIZE_BYTES) {
          ctx.issues.push({
            code: 'custom',
            input: value.file.size,
            message: MAX_ATTACHMENT_SIZE_ERROR_MESSAGE,
            path: ['file'],
          });
        }
      }
    });
